import React, { useState } from 'react';
import type { Item } from '@/stores/canvasStore';
import { toStickyColorName } from '@/lib/stickyColors';
import { getFolderColorFromValue } from '@/lib/folderColors';
import { paperLabel } from '@/lib/folderPreview';

interface FolderPaperProps {
  item: Item;
  /** Folder width / 200: keeps type legible as the folder is resized. */
  scale: number;
  textColor: string;
}

type Content = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

/** Cover image for a paper, when the item has one worth showing. */
function coverOf(item: Item): string | null {
  const c = (item.content ?? {}) as Content;
  switch (item.item_type) {
    case 'image':
      return str(c.url) || null;
    case 'video':
      return str(c.thumbnailUrl) || null;
    case 'link':
      return str(c.image) || str(c.screenshot) || null;
    case 'scraped_creative':
      return str(c.thumbnail_url) || null;
    default:
      return null;
  }
}

/**
 * A tiny read-only rendering of a canvas item, sized to sit on a paper in a
 * folder. Shows the thing itself (the image, the sticky's words, the doc's
 * title) rather than an icon, so a folder tells you what it holds.
 */
export function FolderPaper({ item, scale, textColor }: FolderPaperProps) {
  const [coverFailed, setCoverFailed] = useState(false);
  const c = (item.content ?? {}) as Content;
  const label = paperLabel(item);
  const cover = coverFailed ? null : coverOf(item);
  const pad = 6 * scale;
  const fontSize = Math.max(5, 7.5 * scale);

  const text = (lines: number, weight = 500, extra?: React.CSSProperties) => (
    <span
      style={{
        display: '-webkit-box',
        WebkitLineClamp: lines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        fontSize,
        lineHeight: 1.3,
        fontWeight: weight,
        color: textColor,
        wordBreak: 'break-word',
        ...extra,
      }}
    >
      {label}
    </span>
  );

  if (item.item_type === 'sticky') {
    return (
      <div
        className="h-full w-full"
        style={{ background: `hsl(var(--sticky-${toStickyColorName(str(c.color))}))`, padding: pad }}
      >
        {text(6, 500, { color: 'hsl(var(--sticky-text))' })}
      </div>
    );
  }

  if (item.item_type === 'board_link') {
    const palette = getFolderColorFromValue(str(c.color));
    return (
      <div className="flex h-full w-full flex-col items-center justify-center" style={{ padding: pad, gap: 4 * scale }}>
        <svg viewBox="0 0 120 90" style={{ width: '62%' }} aria-hidden="true">
          <path d="M8 4 Q4 4 4 8 L4 82 Q4 86 8 86 L112 86 Q116 86 116 82 L116 18 Q116 14 112 14 L44 14 Q40 14 38 10 L36 6 Q34 4 30 4 Z" fill={palette.folderDark} />
          <path d="M4 26 Q4 22 8 22 L112 22 Q116 22 116 26 L116 82 Q116 86 112 86 L8 86 Q4 86 4 82 Z" fill={palette.folder} />
        </svg>
        {text(2, 600, { textAlign: 'center' })}
      </div>
    );
  }

  if (cover) {
    const showCaption = item.item_type === 'link';
    return (
      <div className="relative flex h-full w-full flex-col">
        <img
          src={cover}
          alt=""
          draggable={false}
          loading="lazy"
          decoding="async"
          onError={() => setCoverFailed(true)}
          className="min-h-0 w-full flex-1 object-cover"
        />
        {item.item_type === 'video' && (
          <span
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{
              width: 0,
              height: 0,
              borderTop: `${5 * scale}px solid transparent`,
              borderBottom: `${5 * scale}px solid transparent`,
              borderLeft: `${8 * scale}px solid rgba(255,255,255,0.92)`,
              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.35))',
            }}
          />
        )}
        {showCaption && <div style={{ padding: `${4 * scale}px ${pad}px` }}>{text(2, 600)}</div>}
      </div>
    );
  }

  if (item.item_type === 'file') {
    const name = str(c.filename);
    const ext = (str(c.mimeType) === 'application/pdf' ? 'pdf' : name.split('.').pop() || 'file').slice(0, 4).toUpperCase();
    return (
      <div className="flex h-full w-full flex-col" style={{ padding: pad, gap: 4 * scale }}>
        <span
          className="self-start rounded-sm font-semibold tracking-wide"
          style={{
            fontSize: Math.max(4.5, 6 * scale),
            padding: `${1 * scale}px ${3 * scale}px`,
            background: 'hsl(var(--wash))',
            color: 'hsl(var(--ink-3))',
          }}
        >
          {ext}
        </span>
        {text(4, 600)}
      </div>
    );
  }

  if (item.item_type === 'text_card' || item.item_type === 'document') {
    const title = str(c.title);
    const body = stripHtml(str(c.body));
    return (
      <div className="flex h-full w-full flex-col" style={{ padding: pad, gap: 3 * scale }}>
        {title && text(2, 650)}
        {body && (
          <span
            style={{
              display: '-webkit-box',
              WebkitLineClamp: title ? 5 : 7,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: fontSize * 0.86,
              lineHeight: 1.35,
              color: textColor,
              opacity: 0.62,
            }}
          >
            {body}
          </span>
        )}
        {!title && !body && text(2, 600)}
      </div>
    );
  }

  return (
    <div className="h-full w-full" style={{ padding: pad }}>
      {text(6, 500)}
    </div>
  );
}
